<template>
  <q-btn-dropdown
    align="left"
    class="dropdown-btn full-width"
    content-class="box-shadow"
    menu-anchor="bottom start"
    menu-self="top left"
    :disable="disabled"
    @show="focusFilterInput"
  >
    <template #label>
      <user-avatar class="q-mr-sm" :full-name="btnLabel" :url="avatarUrl" />
      <div v-if="innerLabel && btnLabel" class="row items-center no-wrap full-width relative-position">
        <div class="inner-label">{{ innerLabel }}</div>
        <div
          class="text-left text-active dropdown-text"
          :class="{
            'clearable-icon': clearable,
            'text-negative': errorHighlight,
          }"
        >
          <br />{{ btnLabel }}
        </div>
      </div>
      <div
        v-else
        class="text-left dropdown-text"
        :class="{
          'clearable-icon': clearable,
          'text-negative': errorHighlight,
        }"
      >
        {{ btnLabel || label }}
      </div>
      <q-icon
        v-if="clearable && btnLabel"
        name="clear"
        class="clear-dropdown-icon"
        data-testid="dropdown-clear-btn"
        @click="clearSelection($event)"
      />
    </template>
    <q-list :data-testid="identifier" bordered dense>
      <q-item v-show="showSearch">
        <q-item-section>
          <q-input ref="refFilterText" v-model="filterText" color="input" :label="t('search')" borderless>
            <template #prepend>
              <q-icon name="search" />
            </template>
            <template #append>
              <q-icon name="close" class="cursor-pointer" @click="filterText = ''" />
            </template>
          </q-input>
        </q-item-section>
      </q-item>
      <q-separator />
      <template v-for="(item, index) of filteredItems" :key="item.name + item.value">
        <q-item v-close-popup clickable :dropdown-item="item.name" @click="selectItem(item)">
          <q-item-section avatar>
            <user-avatar :full-name="item.name" :url="item.avatarUrl" />
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ item.name }}</q-item-label>
          </q-item-section>
        </q-item>
        <q-separator v-if="index !== items.length" />
      </template>
      <common-alert
        v-if="filteredItems.length === 0"
        type="info"
        show
        hide-close-button
        class="q-pa-sm"
        translate-message="common.noItemsFound"
      />
    </q-list>
  </q-btn-dropdown>
</template>

<script lang="ts" setup>
import { ref, toRefs, watch, onMounted, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { UserDropdownItem } from '@/components/_common/dropdown/DropdownItem';
import { normalizeString } from '@/utils';
import UserAvatar from '@/components/_common/UserAvatar.vue';
import CommonAlert from '@/components/_common/CommonAlert.vue';

/*****************************************************************************
 * PROPS
 ****************************************************************************/
const props = withDefaults(
  defineProps<{
    items?: UserDropdownItem[];
    identifier?: string;
    label?: string;
    innerLabel?: string;
    selectedItemValue?: string | number | null;
    nullItem?: boolean;
    clearable?: boolean;
    errorHighlight?: boolean;
    showSearch?: boolean;
    disabled?: boolean;
  }>(),
  {
    items: () => [],
    identifier: '',
    label: '',
    innerLabel: '',
    selectedItemValue: null,
    nullItem: false,
    clearable: false,
    errorHighlight: false,
    showSearch: true,
    disabled: false,
  },
);

/*****************************************************************************
 * EMITS
 ****************************************************************************/
const emit = defineEmits(['update:selectedItemValue']);

/*****************************************************************************
 * SETUP
 ****************************************************************************/
const { selectedItemValue, items, nullItem } = toRefs(props);
const { t } = useI18n();
const nullDropDownItem: UserDropdownItem = {
  name: t('none'),
  value: null,
};

const btnLabel = ref('');
const avatarUrl = ref('');

const clearSelection = (event: Event) => {
  event.stopPropagation();
  btnLabel.value = '';
  avatarUrl.value = '';
  emit('update:selectedItemValue', '');
};
const selectItem = (item: UserDropdownItem) => {
  btnLabel.value = item.name;
  avatarUrl.value = item.avatarUrl as string;
  emit('update:selectedItemValue', item.value);
};
const setSelectedItem = (searchItemValue: string | number | null): void => {
  const selectedItem = items.value.find((item) => item.value === searchItemValue);
  if (selectedItem) {
    btnLabel.value = selectedItem.name;
    avatarUrl.value = selectedItem.avatarUrl as string;
  }
};

const addNullItem = () => {
  if (nullItem?.value && items.value[0]?.value !== null) {
    items.value.unshift(nullDropDownItem);
  }
};

watch(selectedItemValue, () => {
  setSelectedItem(selectedItemValue.value);
});

watch(items, () => {
  setSelectedItem(selectedItemValue.value);
  addNullItem();
});

onMounted(() => {
  setSelectedItem(selectedItemValue.value);
  addNullItem();
});

const filterText = ref('');
const filteredItems = computed(() => {
  return items.value.filter((item) => {
    if (filterText.value === '') {
      return true;
    }
    return normalizeString(`${item.name.toLocaleLowerCase()}${item.search?.toLocaleLowerCase()}`).includes(
      normalizeString(filterText.value).toLocaleLowerCase(),
    );
  });
});

const refFilterText = ref();
const focusFilterInput = () => {
  nextTick(() => refFilterText.value.focus());
};
</script>

<style lang="scss" scoped>
@import 'styles/app.variables';
.dropdown-text {
  width: calc(100% - 18px);
  overflow: hidden;
  text-overflow: ellipsis;
  &.clearable-icon {
    width: calc(100% - 45px);
  }
}
body.body--dark {
  .q-icon {
    color: nth($text-color, 1) !important;
  }
}
body.body--light {
  .q-icon {
    color: nth($text-color, 1) !important;
  }
}
.dropdown-btn {
  padding-left: 10px;
}
</style>
